import { forwardRef, useCallback, useState } from 'react';
import type { Ref } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import type { TextButtonSize } from '@opendoor/tokens-studio/dist/bricks-next/types/Components';
import { styled, Text as TamaguiText, useMedia, useProps, View } from '@tamagui/core';
import type { GetProps, TamaguiElement, WithMediaProps } from '@tamagui/core';

import { ChevronLeft, ChevronRight } from '../../__generated__/icons';
import { getStateTokenValue } from '../../helpers/tokens';
import { createAnchorOrButtonFrame } from '../shared/anchorOrButton';
import { Text } from '../Text';

const { textButton: textButtonTokens, button: buttonTokens } = tokens;

export type TextButtonVariant = Exclude<keyof typeof textButtonTokens.color.content, 'state'>;

const sharedFrameConfig = {
  name: 'TextButton',

  display: 'flex',
  flexDirection: 'row',
  gap: textButtonTokens.padding.labelIconGap.default,
  userSelect: 'none',
  cursor: 'pointer',

  // Browser resets
  padding: 0,
  borderWidth: 0,
  backgroundColor: '$transparent',

  focusVisibleStyle: {
    outlineStyle: 'solid',
    outlineWidth: textButtonTokens.borderWidth.state.default,
    outlineOffset: textButtonTokens.padding.state.focused.default,
    borderRadius: textButtonTokens.cornerRadius.state.focused,
  },

  pressStyle: {
    scale: getStateTokenValue(buttonTokens.state.pressedScale),
  },

  variants: {
    variant: (variant: TextButtonVariant) => ({
      focusVisibleStyle: {
        outlineColor:
          variant === 'secondaryInverse'
            ? textButtonTokens.color.border.state.inverse
            : textButtonTokens.color.border.state.default,
      },
    }),

    disabled: {
      true: {
        pointerEvents: 'none',
      },
    },
  },
} as const;

const AnchorFrame = styled(TamaguiText, {
  ...sharedFrameConfig,
  tag: 'a',
  accessibilityRole: 'link',
  textDecorationLine: 'none',
  display: 'flex',
  position: 'relative',
});

type AnchorFrameProps = GetProps<typeof AnchorFrame>;

const ButtonFrame = styled(View, {
  ...sharedFrameConfig,
  tag: 'button',
  accessibilityRole: 'button',
});

type ButtonFrameProps = GetProps<typeof ButtonFrame>;

const AnchorOrButtonFrame = createAnchorOrButtonFrame<AnchorFrameProps | ButtonFrameProps>({
  AnchorFrame,
  ButtonFrame,
});

type AnchorOrButtonFrameProps = GetProps<typeof AnchorOrButtonFrame>;

export type TextButtonProps = Omit<AnchorOrButtonFrameProps, 'children' | 'tag'> & {
  /** TextButton variant */
  variant: TextButtonVariant;
  /** TextButton size */
  size: TextButtonSize;
  /** Text content of the HTML element */
  label: string;
  /** Whether the HTML element is disabled */
  disabled?: boolean;
  /** Optional: whether to show a chevron icon on the left or right */
  chevronIcon?: 'left' | 'right';
  /** Optional: whether to update the fontWeight */
  fontWeight?: keyof (typeof textButtonTokens)['fontWeight'];
  /** Type of the HTML button */
  type?: 'button' | 'submit' | 'reset';
} & WithMediaProps<{
    /** TextButton size */
    size: TextButtonSize;
  }>;

/**
 * The TextButton component is a pressable element that's styled to be
 * less-pronounced than the regular Button component. The component is similar
 * to the "nav" variant of the Link component in the old version of bricks. In
 * this version of bricks, TextButton and Link are separate components. Use Link
 * when the clickable text should be inline with other text (e.g., in the middle
 * of a paragraph), and use TextButton when the clickable text should be set off
 * on its own. With TextButton, you can also pass a `chevronIcon` prop to show a
 * chevron on either the left or the right side of the button text.
 *
 * Like Button, TextButton accepts an `href` prop that lets you use the
 * component for URL-based navigation. Refer to the Button component docs for
 * more information. Also refer to the Button docs for information on flexbox
 * and analytics props.
 */
export const TextButton = forwardRef(function TextButton(
  {
    variant,
    disabled,
    label,
    onHoverIn,
    onHoverOut,
    chevronIcon,
    fontWeight,
    ...rest
  }: TextButtonProps,
  ref: Ref<TamaguiElement>,
) {
  const { size = 'medium' } = useProps(rest);

  const [isHovering, setIsHovering] = useState(false);

  const { hoverNone } = useMedia();

  const handleHoverIn = useCallback(
    (e: Parameters<NonNullable<typeof onHoverIn>>[0]) => {
      !hoverNone && setIsHovering(true);
      onHoverIn?.(e);
    },
    [hoverNone, onHoverIn],
  );

  const handleHoverOut: typeof onHoverOut = useCallback(
    (e: Parameters<NonNullable<typeof onHoverIn>>[0]) => {
      !hoverNone && setIsHovering(false);
      onHoverOut?.(e);
    },
    [hoverNone, onHoverOut],
  );

  const color = disabled
    ? textButtonTokens.color.content.state.inactive[variant]
    : isHovering
      ? textButtonTokens.color.content.state.hover[variant]
      : textButtonTokens.color.content[variant];

  return (
    <AnchorOrButtonFrame
      variant={variant}
      disabled={disabled}
      aria-disabled={disabled}
      focusable={!disabled}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      ref={ref}
      {...rest}
    >
      {chevronIcon === 'left' && <ChevronLeft size={size === 'large' ? 24 : 20} color={color} />}
      <Text
        tag="span"
        typography={textButtonTokens.typography[size]}
        textDecorationLine={variant === 'primary' || chevronIcon ? 'none' : 'underline'}
        color={color}
        fontWeight={fontWeight ? textButtonTokens.fontWeight[fontWeight] : undefined}
      >
        {label}
      </Text>
      {chevronIcon === 'right' && <ChevronRight size={size === 'large' ? 24 : 20} color={color} />}
    </AnchorOrButtonFrame>
  );
});
